import { appSettingsFailureState, appSettingsSuccessState, authStatus } from './services/redux/selectors';
import { resetMessage } from './utils/common_functions';
import {
  getSpecializationRequest,
  getCountryListRequest,
  resetAppSettingsMessage,
  getNotificationsRequest,
  getSpecialistFilterOptionsRequest,

  // cancel triggers
  cancelPatientDashboardRequest,
  cancelAppSettingsRequest,
  cancelAuthRequest,
  cancelUserRequest,
  cancelChatMessagesRequest,
  getAdminRolesRequest,
  cancelPatientAdminRequest,
  cancelSupportAdminRequest,
  cancelHospitalAdminRequest,
  cancelNotificationRequest,
  cancelSpecialistAdminRequest,
  getLicenceVerificationModeRequest,
  logoutRequest,
} from '@/services/redux/slices';
import { useDispatch, useSelector } from 'react-redux';
import React, { PropsWithChildren, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { SessionExpirationModal } from '@/components';
import { useRole } from '@/hooks/useRole';
import { ensureWebSocketConnected } from './context/webSocketBridge';

/**
 * this component is used ot fetch global app settings from backend via public apis.
 * It also tracks user activity (cursor movement, clicks, keyboard, scroll) and shows
 * an inactivity modal if no activity is detected:
 * - 15 minutes for Specialist/Patient/POC Level Admin
 * - 10 minutes for All Admins and Super Admin
 */

export const AppSettings: React.FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useDispatch();
  const failure = useSelector(appSettingsFailureState);
  const success = useSelector(appSettingsSuccessState);
  const isAuthenticated = useSelector(authStatus);
  const { isPatient, isSpecialist, isHospitalPoc, isAdmin, isSuperAdmin } = useRole();
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const autoLogoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  const webSocketDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const modalOpenRef = useRef<boolean>(false);
  const [showInactivityModal, setShowInactivityModal] = useState(false);
  const WEBSOCKET_CONNECT_DEBOUNCE_MS = 1000;

  // Calculate inactivity timeout based on user role
  const INACTIVITY_TIMEOUT = useMemo(() => {
    if (isPatient || isSpecialist || isHospitalPoc) {
      // return 30 * 60 * 1000;
      return 15 * 60 * 1000;

    }
    if (isAdmin || isSuperAdmin) {
      // return 30 * 60 * 1000;
      return 10 * 60 * 1000;

    }
    // Default to 10 minutes if role is not determined
    // return 30 * 60 * 1000;
    return 10 * 60 * 1000;
  }, [isPatient, isSpecialist, isHospitalPoc, isAdmin, isSuperAdmin]);

  // Activity tracking function
  const debounceEnsureWebSocketConnected = useCallback(() => {
    if (webSocketDebounceTimerRef.current) {
      clearTimeout(webSocketDebounceTimerRef.current);
    }

    webSocketDebounceTimerRef.current = setTimeout(() => {
      ensureWebSocketConnected();
      webSocketDebounceTimerRef.current = null;
    }, WEBSOCKET_CONNECT_DEBOUNCE_MS);
  }, []);

  const resetInactivityTimer = useCallback(() => {
    debounceEnsureWebSocketConnected();
    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (autoLogoutTimerRef.current) {
      clearTimeout(autoLogoutTimerRef.current);
      autoLogoutTimerRef.current = null;
    }

    // Only set new timer if modal is not open
    const effectiveInactivityTimer = INACTIVITY_TIMEOUT - 60000; // 1 minute before the timeout
    if (!modalOpenRef.current) {
      inactivityTimerRef.current = setTimeout(() => {
        setShowInactivityModal(true);
        modalOpenRef.current = true;
      }, effectiveInactivityTimer);
    }

    autoLogoutTimerRef.current = setTimeout(() => {
      setShowInactivityModal(false);
      dispatch(logoutRequest());
    }, INACTIVITY_TIMEOUT);
  }, [INACTIVITY_TIMEOUT, debounceEnsureWebSocketConnected]);

  // Update ref when modal state changes
  useEffect(() => {
    modalOpenRef.current = showInactivityModal;
  }, [showInactivityModal]);

  // Set up activity tracking listeners
  useEffect(() => {
    // Only set up activity tracking if user is authenticated
    if (!isAuthenticated) {
      return;
    }

    // List of events to track for user activity
    const activityEvents = ['mousemove', 'mousedown', 'mouseup', 'click', 'keydown', 'keypress', 'keyup', 'scroll', 'touchstart', 'touchmove', 'touchend'];

    // Handler that resets timer only if modal is not open
    const handleActivity = () => {
      if (!modalOpenRef.current) {
        resetInactivityTimer();
      }
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Initialize the timer
    resetInactivityTimer();

    // Cleanup function
    return () => {
      // Remove event listeners
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });

      // Clear timer
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
    };
  }, [resetInactivityTimer, isAuthenticated]);

  const handleCloseModal = useCallback(() => {
    setShowInactivityModal(false);
    modalOpenRef.current = false;
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  useEffect(() => {
    // fecth specialization list all app load
    dispatch(getSpecializationRequest());
    // fetch country list on app load
    dispatch(getCountryListRequest());
    // fetch admin roles on app load
    dispatch(getAdminRolesRequest());
    // fetch specialist filter options on app load
    dispatch(getSpecialistFilterOptionsRequest());
    // fetch notifications on app load
    if (isAuthenticated) {
      dispatch(getNotificationsRequest({ page: 1 }));
      // fetch licence verification mode on app load (admin settings)
      dispatch(getLicenceVerificationModeRequest());
    }
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    return () => resetMessage(() => dispatch(resetAppSettingsMessage()));
  }, [failure, success, dispatch]);

  useEffect(() => {
    return () => {
      if (webSocketDebounceTimerRef.current) {
        clearTimeout(webSocketDebounceTimerRef.current);
        webSocketDebounceTimerRef.current = null;
      }
    };
  }, []);

  // Cancel all Axios requests on page unload
  useEffect(() => {
    dispatch(cancelPatientDashboardRequest());
    dispatch(cancelAppSettingsRequest());
    dispatch(cancelAuthRequest());
    dispatch(cancelUserRequest());
    dispatch(cancelChatMessagesRequest());
    dispatch(cancelPatientAdminRequest());
    dispatch(cancelSupportAdminRequest());
    dispatch(cancelHospitalAdminRequest());
    dispatch(cancelNotificationRequest());
    dispatch(cancelSpecialistAdminRequest());
  }, [dispatch]);

  return (
    <>
      {children}
      <SessionExpirationModal open={showInactivityModal} onClose={handleCloseModal} />
    </>
  );
};
